import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, Banknote, Building2, Globe2, FileText, Users, Scale, Calculator, PiggyBank, Receipt, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1777229514251-f946a6833921?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGJ1aWxkaW5nJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3ODMyNDI5OHww&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.unsplash.com/photo-1776704982763-b28e9301559d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGJ1aWxkaW5nJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3ODMyNDI5OHww&ixlib=rb-4.1.0&q=85";

const HIGHLIGHTS = [
  { icon: Building2, t: "Incorporations", d: "Pvt Ltd · LLP · OPC · Society / NGO · USA LLC / C-Corp / S-Corp" },
  { icon: FileText, t: "Tax & Compliance", d: "Income Tax · GST · TDS · PF · ESIC · ROC · MLFW" },
  { icon: Banknote, t: "Loans & Funding", d: "Business · Home · Personal · LAP · Private funding" },
  { icon: Globe2, t: "USA Practice", d: "MSME accounting · EIN · Sales tax · Annual filings" },
  { icon: Users, t: "CA Marketplace", d: "Verified CAs list services. Platform fee 15%." },
  { icon: Sparkles, t: "AI Q&A · Debates", d: "GPT-5 · Claude 4.5 · Gemini 3. Cited answers." },
];

export default function Landing() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grain" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/85 to-white" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-blue-700 font-bold mb-6">
              <ShieldCheck className="w-4 h-4" /> Aloka · India + USA · Since 2020
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tighter text-slate-900">
              Compliance, capital<br/>and corporate counsel.<br/>
              <span className="text-blue-600">Without the friction.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-slate-600 leading-relaxed">
              File returns, register companies, raise loans and incorporate US entities — through one transparent
              consulting marketplace. Pay only for what you avail. 100% money-back if quality fails.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link to="/services"><Button size="lg" className="btn-primary" data-testid="cta-explore-services">Explore Services <ArrowRight className="w-4 h-4 ml-2"/></Button></Link>
              <Link to="/register?role=professional"><Button size="lg" variant="outline" data-testid="cta-list-as-ca">List as CA / Professional</Button></Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-2 text-sm text-slate-500">
              <span><b className="text-slate-900">15%</b> platform fee · pros keep 85%</span>
              <span><b className="text-slate-900">2,400+</b> filings completed</span>
              <span><b className="text-slate-900">17</b> service categories</span>
            </div>
          </div>
          <div className="md:col-span-4">
            <div className="border border-slate-300 rounded p-6 bg-white">
              <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-bold">Quick Quote</div>
              <h3 className="font-display text-2xl font-bold text-slate-900 mt-2 mb-3">Pricing snapshot</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex justify-between"><span>ITR Filing (Individual)</span><b>₹1,499</b></li>
                <li className="flex justify-between"><span>GST Reg + 1mo Return</span><b>₹2,499</b></li>
                <li className="flex justify-between"><span>Pvt Ltd Incorporation</span><b>₹9,999</b></li>
                <li className="flex justify-between"><span>USA LLC Formation</span><b>$349</b></li>
                <li className="flex justify-between"><span>USA C-Corp</span><b>$499</b></li>
              </ul>
              <Link to="/services"><Button className="w-full mt-5 btn-accent" data-testid="cta-view-all-pricing">View all pricing</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="highlights-section">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-bold mb-3">What we do</div>
            <h2 className="font-display text-4xl font-black tracking-tight text-slate-900">A full back-office for ambitious founders.</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 self-end text-slate-600 leading-relaxed">
            From your first GST return to your Delaware C-Corp, Aloka pairs you with verified CAs and runs the
            paperwork end-to-end. Get a finance community, AI-powered answers, and human review — in one place.
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {HIGHLIGHTS.map((h, i) => (
            <div key={i} className="tracing-card border border-slate-200 p-6 rounded bg-white" data-testid={`highlight-${i}`}>
              <h.icon className="w-6 h-6 text-blue-600 mb-4" />
              <h4 className="font-bold text-lg text-slate-900 mb-1">{h.t}</h4>
              <p className="text-sm text-slate-600">{h.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW */}
      <section className="bg-slate-50 border-y border-slate-200 py-20" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-bold mb-3">How it works</div>
              <h2 className="font-display text-4xl font-black tracking-tight text-slate-900">Four steps. Zero ambiguity.</h2>
              <img src={ABOUT_IMG} alt="" className="mt-8 rounded border border-slate-200" />
            </div>
            <ol className="md:col-span-6 md:col-start-7 space-y-6">
              {[
                ["Register", "Sign up as Client or CA / Professional. Email or Google."],
                ["Pick service", "Choose from 16+ India & USA services with fixed pricing."],
                ["Upload & Pay", "Upload KYC and pay via Stripe (USD) or Razorpay (INR)."],
                ["Receive & Refund", "Track delivery. Not satisfied? Aloka reviews quality and decides refund."],
              ].map(([t, d], i) => (
                <li key={i} className="flex gap-4">
                  <span className="font-mono text-3xl font-black text-blue-600 leading-none w-12">0{i+1}</span>
                  <div>
                    <div className="font-bold text-slate-900">{t}</div>
                    <div className="text-sm text-slate-600">{d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* COMMUNITY TEASER */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="community-teaser">
        <div className="border border-slate-200 rounded p-10 bg-white grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-bold mb-3">Community</div>
            <h2 className="font-display text-4xl font-black text-slate-900 mb-3">Ask. Debate. Get cited answers.</h2>
            <p className="text-slate-600 mb-6">A finance forum where founders ask, CAs answer, and three frontier
              AI models — GPT-5, Claude Sonnet 4.5, Gemini 3 Flash — debate the nuances with sources.</p>
            <div className="flex gap-3">
              <Link to="/community"><Button className="btn-primary" data-testid="cta-community">Ask a question <Scale className="w-4 h-4 ml-2"/></Button></Link>
              <Link to="/debate"><Button variant="outline" data-testid="cta-debate">See AI debates</Button></Link>
            </div>
          </div>
          <div className="border border-slate-200 rounded p-5 bg-blue-50">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700 mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5"/>AI Answer · GPT-5</div>
            <p className="text-sm text-slate-700 leading-relaxed">
              <b>Q:</b> Should an Indian SaaS founder incorporate a Delaware C-Corp before raising US VC?<br/>
              <b>A:</b> A flip to Delaware C-Corp is typical for institutional US VC, but understand the FEMA &
              Section 56(2)(viib) implications before…
            </p>
            <div className="text-[11px] text-slate-500 mt-2">Sources: Companies Act 2013 §149, FEMA 1999, IRS Form 5471</div>
          </div>
        </div>
      </section>

      {/* TOOLS & QUICK LINKS */}
      <section className="bg-slate-50 border-y border-slate-200 py-20" data-testid="tools-teaser">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-10 mb-10 items-end">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-bold mb-3">Free tools</div>
              <h2 className="font-display text-4xl font-black tracking-tight text-slate-900">EMI · FD · RD calculators and a directory of every gov portal you'll need.</h2>
            </div>
            <div className="md:col-span-5 text-slate-600 leading-relaxed">
              Plan a loan, project a fixed deposit, or jump directly to Income Tax, GST, NSDL, TRACES, EPFO, ESIC, MCA, MLWF — all from one shelf.
              <div className="mt-5"><Link to="/tools"><Button size="lg" className="btn-primary" data-testid="cta-tools">Open Tools <ArrowRight className="w-4 h-4 ml-2"/></Button></Link></div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {icon: Calculator, t: "EMI Calculator", d: "Home, Business, Personal & LAP loans — monthly EMI, total interest and payback.", to: "/tools"},
              {icon: PiggyBank,  t: "FD / RD Calculator", d: "Maturity & interest with monthly/quarterly compounding presets.", to: "/tools"},
              {icon: Receipt,    t: "Gov Quick Links", d: "Income Tax, GST, NSDL, TRACES, EPFO, ESIC, MCA, MLWF, IRS & SOS portals.", to: "/tools"},
            ].map((c,i) => (
              <Link key={i} to={c.to} className="tracing-card border border-slate-200 p-6 rounded bg-white block" data-testid={`tools-card-${i}`}>
                <c.icon className="w-6 h-6 text-blue-600 mb-4"/>
                <h4 className="font-bold text-lg text-slate-900 mb-1">{c.t}</h4>
                <p className="text-sm text-slate-600">{c.d}</p>
                <div className="text-xs uppercase tracking-widest text-blue-600 font-bold mt-4 inline-flex items-center">Open <ArrowRight className="w-3 h-3 ml-1"/></div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
